import React, { useRef, useState } from "react";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { DropdownMenu } from "components/common/DropdownMenu";
import { MenuItem } from "components/common/MenuItem";
import { Spinner } from "components/common/Spinner";
import { Tooltip } from "components/common/Tooltip";
import {
  IMAGE_ACCEPT_STRING,
  IMAGE_LIMITS,
  imageRefusal,
  resizeImage,
} from "@/lib/images";
import { message } from "@/lib/serverError";

/// A row led by an uploadable picture: the account photo, an org's image.
/// With none set, the picture opens the file picker; with one, a menu of
/// Upload and Remove. A file dropped on the row uploads too. Every change
/// lands at once, and a refusal shows under `children`.
export const ImageRow: React.FC<{
  picture: React.ReactNode;
  hasImage: boolean;
  noun: "photo" | "image";
  label: string;
  canChange?: boolean;
  onUpload: (dataUrl: string) => Promise<unknown>;
  onRemove: () => Promise<unknown>;
  className?: string;
  children: React.ReactNode;
}> = ({
  picture,
  hasImage,
  noun,
  label,
  canChange = true,
  onUpload,
  onRemove,
  className = "",
  children,
}) => {
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [dropping, setDropping] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pick = () => inputRef.current?.click();

  const run = async (write: () => Promise<unknown>, failure: string) => {
    setBusy(true);
    setRefusal(null);
    try {
      await write();
    } catch (err) {
      setRefusal(message(err, failure));
    } finally {
      setBusy(false);
    }
  };

  const upload = (file: File) => {
    if (busy) return;
    const refused = imageRefusal(file);
    if (refused) return setRefusal(refused);
    void run(async () => {
      const image = await resizeImage(file).catch(() => {
        throw new Error(`${file.name} could not be read as an image`);
      });
      await onUpload(image);
    }, "Upload failed");
  };

  const button = (
    <button
      ref={buttonRef}
      type="button"
      className="avatar-button"
      aria-label={label}
      aria-haspopup={hasImage ? "menu" : undefined}
      aria-expanded={hasImage ? menuOpen : undefined}
      aria-busy={busy || undefined}
      data-dropping={dropping || undefined}
      disabled={!canChange}
      onClick={() => {
        if (busy) return;
        if (hasImage) setMenuOpen((open) => !open);
        else pick();
      }}
    >
      {picture}
      {canChange && (
        <span className="avatar-button-overlay" aria-hidden>
          {busy ? (
            <Spinner size="sm" />
          ) : (
            <CameraAltOutlinedIcon sx={{ fontSize: 16 }} />
          )}
        </span>
      )}
    </button>
  );

  return (
    <div
      className={`${className} ${dropping ? "rounded-app bg-accent-subtle outline-1 -outline-offset-1 outline-dashed outline-accent" : ""}`}
      onDragOver={(e) => {
        if (!canChange || !e.dataTransfer.types.includes("Files")) return;
        e.preventDefault();
        setDropping(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null))
          setDropping(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDropping(false);
        const file = e.dataTransfer.files[0];
        if (file && canChange) upload(file);
      }}
    >
      {canChange ? (
        <Tooltip title={IMAGE_LIMITS} describeChild>
          {button}
        </Tooltip>
      ) : (
        button
      )}
      <DropdownMenu
        anchorRef={buttonRef}
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        label={label}
        style={{ width: "auto", minWidth: "9rem" }}
      >
        <MenuItem
          Icon={CameraAltOutlinedIcon}
          label={`Upload ${noun}`}
          onClick={() => {
            setMenuOpen(false);
            pick();
          }}
        />
        <MenuItem
          Icon={DeleteOutlineOutlinedIcon}
          label={`Remove ${noun}`}
          onClick={() => {
            setMenuOpen(false);
            void run(onRemove, "Removal failed");
          }}
        />
      </DropdownMenu>
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_ACCEPT_STRING}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) upload(file);
        }}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {children}
        {refusal && (
          <span role="alert" className="text-error text-small">
            {refusal}
          </span>
        )}
      </div>
    </div>
  );
};
